import { Autocomplete, Button, DataTableV2, Field, Text } from "components/commons";
import styles from "../delivery-log.module.scss";
import locale from "localization";
import { useFormContext } from "contexts/form-context";
import useDataTable from "hooks/useDataTable";
import { Add } from "@material-ui/icons";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useAlert, useDynamicFieldsForm, useModal, useMount } from "hooks";
import { SelectProductsModal } from "components/modals";
import Validation from "utils/validation.utils";
import useProducts from "hooks/useProducts";
import { mapDataToListFuel, mapDataToListNonFuel, setVariance } from "./mapper";
import { fuelColumns, nonFuelColumns } from "./columns";
import { StationContext } from "contexts";
import { formatVolume } from "utils";
import ChangeTypeModal from "./change-type-modal";
import { CircularProgress } from "@mui/material";

const DeliveryLogProducts = () => {
  const { station } = useContext(StationContext);
  const { addAlert } = useAlert();
  const {
    allProducts,
    fetch: getAllProducts,
    loading: isLoadingGetAllProducts,
  } = useProducts({ withStationCode: true });
  const {
    fields: rootFields,
    modifyField: modifyRootField,
    modifyForm: modifyRootForm,
    setSubFormsSubmittableState,
    initialState,
  } = useFormContext();
  const { fieldsAsRows, addRows, rowsValues, removeRow, modifyField, modifyForm, clearRows } =
    useDynamicFieldsForm({
      rowId: "sku",
      onChangeIsSubmittable: ({ isFormSubmittable, rowsValues }) => {
        setSubFormsSubmittableState((f) => ({ ...f, products: isFormSubmittable }));
        modifyRootField("products", {
          value: rowsValues,
        });
      },
    });

  // const { request: getShift, loading: isGettingShift } = useApi({
  //   api: getShiftByShiftId,
  // });

  const isFuelOnly = useMemo(
    () => rootFields?.productType?.value === "fuel",
    [rootFields?.productType?.value],
  );

  const filteredByProductTypeAllProducts = useMemo(() => {
    return allProducts.filter((p) => {
      if (isFuelOnly) {
        return p.type === "fuel";
      } else {
        return p.type !== "fuel";
      }
    });
  }, [allProducts, isFuelOnly]);

  const productsOptions = useMemo(() => {
    const selected = rowsValues.map((p) => p.sku);
    return filteredByProductTypeAllProducts
      .filter((p) => p.type === rootFields?.productType?.value)
      .filter((p) => !selected.includes(p.sku))
      .map((p) => ({
        label: p.name,
        value: p.sku,
      }));
  }, [filteredByProductTypeAllProducts, rootFields?.productType?.value, rowsValues]);

  const onRemove = (rowIndex) => {
    removeRow(rowIndex);
    return addAlert({
      title: "Product Removed",
      subtitle: "You have successfully removed the product(s)",
      type: "success",
      interval: 3000,
    });
  };

  const changeProductTypeModal = useModal();
  const selectProductModal = useModal();
  const { table } = useDataTable({
    value: rowsValues || [],
    filter: { initialState: {}, mapper: (param) => param },
    table: {
      key: "products",
      mapper: (products) => {
        if (isFuelOnly) {
          return mapDataToListFuel({
            products,
            productsOptions,
            modifyForm,
            modifyField,
            onRemove,
            fieldsAsRows,
            allProducts,
          });
        }

        return mapDataToListNonFuel({
          fieldsAsRows,
          onRemove,
          products,
          modifyField,
          modifyForm,
        });
      },
      columns: isFuelOnly ? fuelColumns : nonFuelColumns,
    },
  });

  const handleProductEntries = useCallback(
    async (selectedProducts) => {
      selectProductModal.close();
      const stationUgts = station?.stationUgts;
      // const products = new Map(allProducts.map((p) => [p.productId, p]));
      // const dryStocksQuantityReceiving = {};
      // const shiftId = rootFields?.shiftId?.value;
      // if (shiftId && !isFuelOnly) {
      //   const res = await getShift({
      //     shiftId,
      //   });

      //   const computedQuantity = res?.delivery.deliveryProducts.reduce((acc, dp) => {
      //     const sku = dp.product;
      //     const quantity = dp.quantityDelivered || 0;
      //     acc[sku] = (acc[sku] || 0) + quantity;
      //     return acc;
      //   }, {});

      //   const inventories = res?.shift?.inventories
      //     .map((inventory) => {
      //       const product = products.get(inventory.productId);
      //       const sku = product?.sku;
      //       const totalQuantityDelivered = sku ? computedQuantity[sku] || 0 : 0;

      //       return {
      //         ...inventory,
      //         product: product?.name || "-",
      //         variant: product?.variant || "-",
      //         type: product?.type || "-",
      //         totalQuantityDelivered,
      //       };
      //     })
      //     .sort((a, b) => a.product.localeCompare(b.product));

      //   inventories.forEach(
      //     ({
      //       sku,
      //       beginningInventory,
      //       totalQuantityDelivered,
      //       salesInventory,
      //       returnInventory,
      //     }) => {
      //       const expectedEndingInventory = computeExpectedInventory(
      //         beginningInventory,
      //         totalQuantityDelivered,
      //         salesInventory,
      //         returnInventory
      //       );
      //       dryStocksQuantityReceiving[sku] = expectedEndingInventory;
      //     }
      //   );
      // }

      const rows = selectedProducts.map((product) => {
        const maxLiter = stationUgts?.find((ugt) => ugt.productCode === product.sku)?.liter;

        if (isFuelOnly) {
          return [
            {
              name: "sku",
              value: product.sku,
              required: true,
              validations: [Validation.required({ noMessage: true })],
            },
            {
              name: "product",
              value: product.name,
              required: false,
              validations: [],
            },
            {
              name: "quantityBeforeReceivingMm",
              value: product?.quantityBeforeReceivingMm || "",
              required: true,
              decimalPlaces: 4,
              isPriceValue: true,
              max: 5000,
              validations: [Validation.required({ noMessage: true })],
            },
            {
              name: "quantityBeforeReceivingLiter",
              value: product?.quantityBeforeReceivingLiter || "",
              required: true,
              decimalPlaces: 3,
              isPriceValue: true,
              min: 0.0,
              max: 100000,
              validations: [
                Validation.required({ noMessage: true }),
                Validation.min(0.001, {
                  customMessage: (
                    <locale.Populate text={locale.valueShouldNotBe} items={["0.000"]} />
                  ),
                }),
                Validation.max(maxLiter, {
                  customMessage: `Maximum UGT limit is ${formatVolume(maxLiter)}`,
                }),
              ],
            },
            {
              name: "quantityDelivered",
              value: product?.quantityDelivered || "",
              required: true,
              decimalPlaces: 3,
              isPriceValue: true,
              min: 0.0,
              max: 100000,
              validations: [
                Validation.required({ noMessage: true }),
                Validation.min(0.001, {
                  customMessage: (
                    <locale.Populate text={locale.valueShouldNotBe} items={["0.000"]} />
                  ),
                }),
                Validation.max(maxLiter, {
                  customMessage: `Maximum UGT limit is ${formatVolume(maxLiter)}`,
                }),
              ],
            },
            {
              name: "quantityAfterReceivingMm",
              value: product?.quantityAfterReceivingMm || "",
              required: true,
              decimalPlaces: 4,
              isPriceValue: true,
              max: 5000,
              validations: [Validation.required({ noMessage: true })],
            },
            {
              name: "quantityAfterReceivingLiter",
              value: product?.quantityAfterReceivingLiter || "",
              required: true,
              decimalPlaces: 3,
              isPriceValue: true,
              min: 0.0,
              max: 100000,
              validations: [
                Validation.required({ noMessage: true }),
                Validation.min(0.001, {
                  customMessage: (
                    <locale.Populate text={locale.valueShouldNotBe} items={["0.000"]} />
                  ),
                }),
                Validation.max(maxLiter, {
                  customMessage: `Maximum UGT limit is ${formatVolume(maxLiter)}`,
                }),
              ],
            },
          ];
        }

        // const quantityBeforeReceiving =
        //   product?.quantityBeforeReceiving ||
        //   (isNumber(dryStocksQuantityReceiving[product.sku])
        //     ? dryStocksQuantityReceiving[product.sku]
        //     : "");
        // const quantityDelivered = product?.quantityDelivered || "";
        // const quantityAfterReceiving =
        //   product?.quantityAfterReceiving ||
        //   (isNumber(dryStocksQuantityReceiving[product.sku])
        //     ? getQuantityAfterReceiving(
        //         dryStocksQuantityReceiving[product.sku],
        //         product?.quantityDelivered
        //       )
        //     : "");

        const quantityBeforeReceiving = product?.quantityBeforeReceiving || "";
        const quantityDelivered = product?.quantityDelivered || "";
        const quantityAfterReceiving = product?.quantityAfterReceiving || "";

        return [
          {
            name: "sku",
            value: product.sku,
            validations: [],
          },
          {
            name: "product",
            value: product.name,
            required: false,
            validations: [],
          },
          {
            name: "variant",
            value: product.variant,
            required: false,
            validations: [],
          },
          {
            name: "category",
            value: product.category.name,
            required: false,
            validations: [],
          },
          {
            name: "quantityBeforeReceiving",
            value: quantityBeforeReceiving,
            required: true,
            decimalPlaces: 0,
            min: -999999,
            max: 999999,
            validations: [
              Validation.required({ noMessage: true }),
              Validation.max(999999, {
                customMessage: `Please enter up to 6 numbers only.`,
              }),
            ],
          },
          {
            name: "quantityDelivered",
            value: quantityDelivered,
            required: true,
            decimalPlaces: 0,
            min: -999999,
            max: 999999,
            validations: [
              Validation.required({ noMessage: true }),
              // Validation.min(0.01),
              Validation.max(999999, {
                customMessage: `Please enter up to 6 numbers only.`,
              }),
            ],
          },
          {
            name: "quantityAfterReceiving",
            value: quantityAfterReceiving,
            required: true,
            decimalPlaces: 0,
            validations: [
              Validation.required({ noMessage: true }),
              Validation.min(-999999, {
                customMessage: `Please enter up to 6 numbers only.`,
              }),
              Validation.max(999999, {
                customMessage: `Please enter up to 6 numbers only.`,
              }),
            ],
          },
          {
            name: "variance",
            value: setVariance({
              quantityBeforeReceivingNumber: quantityBeforeReceiving,
              quantityDeliveredNumber: quantityDelivered,
              quantityAfterReceivingNumber: quantityAfterReceiving,
            }),
            required: false,
            validations: [],
          },
        ];
      });

      addRows(rows);
    },
    [addRows, isFuelOnly, selectProductModal, station?.stationUgts],
  );

  const showProductSelection = useCallback(() => {
    selectProductModal.show({
      title: locale.selectProducts,
      primary: {
        text: fieldsAsRows.length > 0 ? locale.save : locale.add,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectProductModal]);

  const handleTypeChange = (name, props) => {
    const proceed = async () => {
      clearRows();
      modifyRootForm({
        [name]: props,
        products: {
          value: [],
        },
      });
      changeProductTypeModal.close();
    };

    if (props?.value === rootFields?.productType?.value) return;

    if (props?.value && rowsValues.length > 0) {
      changeProductTypeModal.show({
        title: "",
        primary: {
          text: locale.yes,
          onClick: proceed,
        },
        secondary: {
          text: locale.no,
        },
      });
    } else {
      proceed();
    }
  };

  useMount(async () => {
    await getAllProducts();
  });

  // set default value
  useEffect(() => {
    const products = initialState?.products?.value;
    if (products?.length > 0 && !isLoadingGetAllProducts) {
      const seen = new Set();
      const defaultValue = products
        ?.map((productDelivery) => {
          const product = allProducts?.find((p) => p.sku === productDelivery.product);
          if (!product) return null;
          return {
            ...product,
            ...productDelivery,
          };
        })
        ?.filter((p) => {
          if (!p) return false;
          if (seen.has(p.sku)) return false;
          seen.add(p.sku);
          return true;
        });

      if (defaultValue?.length > 0) {
        handleProductEntries(defaultValue);
      } else {
        modifyRootField("products", {
          value: [],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProducts, initialState?.products?.value, isLoadingGetAllProducts]);

  useEffect(() => {
    const isEmpty = rootFields?.products?.value?.length === 0;
    if (isEmpty) {
      clearRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootFields?.products?.value]);

  return (
    <>
      <Text strong color={"black"} className={styles.sectionTitle}>
        {locale.productDetails}
      </Text>
      <Field
        label={locale.productType}
        labelClassName={styles.fieldLabel}
        childrenClassName={styles.fieldContent}
      >
        <Autocomplete
          {...rootFields.productType}
          placeholder={locale.selectProductType}
          options={[
            {
              value: "fuel",
              label: locale.fuel,
            },
            {
              value: "lubricant",
              label: locale.lubricants,
            },
          ]}
          onChange={handleTypeChange}
          disabled={!rootFields?.shiftId?.value}
        />
      </Field>
      {isLoadingGetAllProducts && <CircularProgress size={20} />}
      {rootFields?.productType?.value && (
        <>
          <DataTableV2
            {...table}
            size="small"
            hidePagination
            hideNoSearchResultRender
            tableVersion="v2"
            striped={true}
            className={styles.tableContainer}
            // loading={isGettingShift}
          />
          <br />
          <Button
            primary
            className={styles.palePurpleButton}
            disabled={isLoadingGetAllProducts || !rootFields?.productType?.value}
            onClick={showProductSelection}
            loading={isLoadingGetAllProducts}
          >
            <div className={styles.btn}>
              <Add fontSize="small" /> {locale.addProduct}
            </div>
          </Button>
        </>
      )}
      <SelectProductsModal
        {...selectProductModal}
        value={rowsValues.map((p) => p.sku)}
        onChange={handleProductEntries}
        coloredProduct={false}
        allProducts={filteredByProductTypeAllProducts}
        excludeProductType={isFuelOnly ? ["lubes", "lpg"] : ["fuel"]}
        excludeCols={isFuelOnly ? ["category", "variant"] : []}
        showFilter={!isFuelOnly}
        className={isFuelOnly && styles.productModal}
        dataTableProps={
          isFuelOnly
            ? {
                hidePagination: true,
              }
            : {}
        }
        filterProps={{
          hiddenInputs: {
            productType: true,
          },
        }}
        required
      />
      <ChangeTypeModal {...changeProductTypeModal} />
    </>
  );
};

export default DeliveryLogProducts;
